/**
 * To be used with the PaymentRequestButtonElement from Stripe.
 */
import { useState, useEffect, useRef } from 'react';
import { useStripe } from '@stripe/react-stripe-js';

/**
 * Payment request button for Apple/Google Pay integrations.
 *
 * @param {Object} options The options.
 * @param {string} options.label The label.
 * @param {string} options.currency The currency.
 * @param {number} options.amount The amount to charge.
 * @param {function} options.onStripeToken Callback function on successful charge.
 * @returns {{paymentRequest: StripePaymentRequest}}
 */
const usePaymentRequestButton = ({
  label = 'Waldo Photos',
  currency = 'usd',
  amount,
  onStripeToken,
}) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);

  // Keep the latest onStripeToken so the `token` listener (bound once below)
  // always calls the current closure. Otherwise it captures stale props/state
  // such as the promo code, which changes after the button is initialized.
  const onStripeTokenRef = useRef(onStripeToken);
  onStripeTokenRef.current = onStripeToken;

  useEffect(() => {
    if (!stripe || !amount || paymentRequest) {
      return;
    }

    const paymentRequestResponse = stripe.paymentRequest({
      country: 'US',
      currency,
      total: {
        label,
        amount,
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    (async () => {
      const result = await paymentRequestResponse.canMakePayment();

      if (result) {
        setPaymentRequest(paymentRequestResponse);
      }
    })();
  }, [stripe, amount, currency, label, paymentRequest]);

  // Keep the total in sync when the amount/currency changes (e.g. a promo code
  // is applied after the button was created). update() is safe here because it
  // only runs while the wallet sheet isn't open.
  useEffect(() => {
    if (!paymentRequest || !amount) {
      return;
    }

    paymentRequest.update({
      currency,
      total: {
        label,
        amount,
      },
    });
  }, [paymentRequest, amount, currency, label]);

  useEffect(() => {
    if (!paymentRequest) {
      return;
    }

    paymentRequest.on('token', async event => {
      await onStripeTokenRef.current?.({
        event,
        token: {
          id: event.token.id,
          email: event.payerEmail,
        },
      });
      event.complete('success');
    });
  }, [paymentRequest]);

  return {
    paymentRequest,
  };
};

export default usePaymentRequestButton;
